import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  CalendarDays,
  FolderKanban,
  Inbox,
  Terminal,
  Settings,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  AudioLines,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCommandStyle } from "@/contexts/CommandStyleContext";
import { useInboxStats } from "@/hooks/useInboxStats";
import { cn } from "@/lib/utils";

export function Sidebar() {
  const { copy } = useCommandStyle();

  const navItems = [
    { path: "/", label: copy.dashboard, icon: Home },
    { path: "/messages", label: copy.inbox, icon: Inbox },
    { path: "/transcripts", label: copy.transcripts, icon: AudioLines },
    { path: "/week", label: copy.calendar, icon: CalendarDays },
    { path: "/projects", label: copy.projects, icon: FolderKanban },
    { path: "/sessions", label: copy.sessions, icon: Terminal },
    { path: "/settings", label: copy.settings, icon: Settings },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { data: inboxStats } = useInboxStats();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebar-collapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", String(collapsed));
  }, [collapsed]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const displayName = user?.email?.split("@")[0] ?? "User";

  return (
    <aside
      className={cn(
        "hidden sm:flex flex-col h-screen bg-card border-r border-border transition-all duration-200 shrink-0",
        collapsed ? "w-16" : "w-52"
      )}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Logo / Brand */}
      <div className={cn("flex items-center px-4 h-14 border-b border-border", collapsed && "justify-center")}>
        {!collapsed && (
          <span className="text-sm font-bold tracking-tight truncate">Hella Organized</span>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className={cn(
            "text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            !collapsed && "ml-auto"
          )}
        >
          {collapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-2 px-2 space-y-1">
        {navItems.map(({ path, label, icon: Icon }) => {
          const active = isActive(path);
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                active
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
                collapsed && "justify-center px-0"
              )}
              title={collapsed ? label : undefined}
            >
              <div className="relative shrink-0">
                <Icon className="h-4.5 w-4.5" />
                {path === "/messages" && inboxStats && inboxStats.total > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-accent text-[9px] font-bold text-white flex items-center justify-center">
                    {inboxStats.total > 9 ? "9+" : inboxStats.total}
                  </span>
                )}
              </div>
              {!collapsed && <span>{label}</span>}
            </button>
          );
        })}
      </nav>

      {/* User section */}
      <div className={cn("px-2 py-3 border-t border-border", collapsed && "flex flex-col items-center")}>
        {!collapsed && (
          <div className="flex items-center gap-2 px-3 mb-2">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
              {displayName[0]?.toUpperCase()}
            </div>
            <span className="text-xs text-muted-foreground truncate">{displayName}</span>
          </div>
        )}
        <button
          onClick={signOut}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors",
            collapsed && "justify-center px-0"
          )}
          title={collapsed ? "Sign out" : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  );
}
